/**
 * Month-end loan pricing confirmed with Mosa (Oct 2026).
 * Keep in step with backend/src/utils/loanPricing.ts.
 */

const MONTHLY_INTEREST_RATE = 0.05;
const ADMIN_FEE_PER_30_DAYS = 60;
const VAT_RATE = 0.15;
const INITIATION_FEE = 50;
const DAY_DIVISOR = 30;

export interface LoanQuote {
  days: number;
  interestRate: number;
  interestAmount: number;
  adminFeeExclVat: number;
  vatAmount: number;
  adminFee: number;
  initiationFee: number;
  totalAmount: number;
  repaymentDate: string;
  repaymentDateLabel: string;
}

function roundMoney(amount: number): number {
  return Math.round(amount * 100) / 100;
}

function johannesburgParts(date: Date): { year: number; month: number; day: number } {
  const parts = new Intl.DateTimeFormat('en-ZA', {
    timeZone: 'Africa/Johannesburg',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const read = (type: string) => Number(parts.find((part) => part.type === type)?.value);
  return { year: read('year'), month: read('month'), day: read('day') };
}

export function daysUntilMonthEnd(date: Date = new Date()): number {
  const { year, month, day } = johannesburgParts(date);
  const lastDay = new Date(year, month, 0).getDate();
  return Math.max(0, lastDay - day);
}

export function quoteLoan(principal: number, loanDate: Date = new Date()): LoanQuote {
  const { year, month } = johannesburgParts(loanDate);
  const days = daysUntilMonthEnd(loanDate);
  const fraction = days / DAY_DIVISOR;
  const interestAmount = roundMoney(principal * fraction * MONTHLY_INTEREST_RATE);
  const adminFeeExclVat = roundMoney(ADMIN_FEE_PER_30_DAYS * fraction);
  const vatAmount = roundMoney(adminFeeExclVat * VAT_RATE);
  const adminFee = roundMoney(adminFeeExclVat + vatAmount);
  const lastDay = new Date(year, month, 0).getDate();
  const repaymentDate = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
  const repaymentDateLabel = new Date(year, month - 1, lastDay).toLocaleDateString('en-ZA', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  return {
    days,
    interestRate: MONTHLY_INTEREST_RATE * 100,
    interestAmount,
    adminFeeExclVat,
    vatAmount,
    adminFee,
    initiationFee: INITIATION_FEE,
    totalAmount: roundMoney(principal + interestAmount + adminFee + INITIATION_FEE),
    repaymentDate,
    repaymentDateLabel,
  };
}

export function formatRand(amount: number): string {
  return `R${amount.toFixed(2)}`;
}
